import { FC, Fragment, useRef, useState, useEffect } from "react";
import TextEdit from "./TextEdit";
import { IoCloudUploadOutline } from 'react-icons/io5';

type FileUploadProps = {
    id?: string;
    name: string;
    accept: string;
    label: string;
    canRemove?: boolean;
    errorMessage: string;
    info?: string;
    onUploadFile: (file: File) => void;
    onRemoveFile?: () => void;
    disabled?: boolean;
    multiple?: boolean;
}

const FileUpload: FC<FileUploadProps> = ({ onUploadFile, onRemoveFile = () => { }, accept = "*",
    canRemove = true, errorMessage, info, label, id = "dropfile", name, disabled = false, multiple = false }) => {

    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [isDragActive, setIsDragActive] = useState(false);

    const error = errorMessage ? true : false;
    const borderColor = error ? "border-red-600" : "border-gray-300";

    useEffect(() => {
        const preventDefaults = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
        };

        window.addEventListener('dragover', preventDefaults);
        window.addEventListener('drop', preventDefaults);

        return () => {
            window.removeEventListener('dragover', preventDefaults);
            window.removeEventListener('drop', preventDefaults);
        };
    }, []);

    function handleDrop(e: any) {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        if (e.dataTransfer.files && !disabled) {
            const newFiles = Array.from(e.dataTransfer.files) as File[];
            if (multiple) {
                setUploadedFiles(prev => [...prev, ...newFiles]);
                newFiles.forEach((file: File) => {
                    onUploadFile(file);
                });
            } else {
                const file = newFiles[0];
                if (file) {
                    setUploadedFiles([file]);
                    onUploadFile(file);
                }
            }
        }
    }

    function handleDragLeave(e: any) {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    }

    function handleDragOver(e: any) {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setIsDragActive(true);
        }
    }

    function handleDragEnter(e: any) {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setIsDragActive(true);
        }
    }

    const changeFile = (e: any) => {
        const newFiles = Array.from(e.target.files) as File[];
        if (multiple) {
            setUploadedFiles(prev => [...prev, ...newFiles]);
            newFiles.forEach((file: File) => {
                onUploadFile(file);
            });
        } else {
            const file = newFiles[0];
            if (file) {
                setUploadedFiles([file]);
                onUploadFile(file);
            }
        }
    }

    const removeFile = (index: number) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
        if (uploadedFiles.length === 1) {
            onRemoveFile();
        }
    }

    const removeAllFiles = () => {
        setUploadedFiles([]);
        onRemoveFile();
    }

    return (
        <Fragment>
            <div className="space-y-2">
                {uploadedFiles.length > 0 && (
                    <div className="space-y-2">
                        {multiple && (
                            <div className="flex justify-between items-center">
                                <span className="font-medium">{label} ({uploadedFiles.length})</span>
                                {canRemove && (
                                    <button
                                        type='button'
                                        className="text-red-500 cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={removeAllFiles}
                                        disabled={disabled}
                                    >
                                        Remover todos
                                    </button>
                                )}
                            </div>
                        )}
                        {uploadedFiles.map((file, index) => (
                            <div key={index} className="grid grid-cols-12 gap-4">
                                <div className='col-span-11'>
                                    <TextEdit
                                        label={multiple ? "" : label}
                                        value={file.name}
                                        errorMessage={errorMessage}
                                        disabled
                                    />
                                </div>
                                {canRemove && (
                                    <button
                                        type='button'
                                        className="text-red-500 cursor-pointer col-span-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={() => removeFile(index)}
                                        disabled={disabled}
                                    >
                                        Remover
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                
                <div className="flex items-center justify-center w-full"
                    onDragEnter={handleDragEnter}
                    onDrop={handleDrop}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                >
                    <label htmlFor={id}
                        className={`${isDragActive ? "border-blue-600 bg-blue-50 dark:bg-blue-900/20" : borderColor} 
                    flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 dark:bg-slate-800 dark:hover:bg-slate-700 transition-colors`}
                    >
                        <div
                            className="flex flex-col items-center justify-center pt-5 pb-6"
                        >
                            <IoCloudUploadOutline className="text-4xl text-gray-500 " />
                            <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                <span className="font-semibold">Clique aqui para subir {multiple ? 'arquivos' : 'arquivo'}</span> ou arraste e solte aqui.
                            </p>
                            {info ? <p className="text-xs text-gray-500 dark:text-gray-400">{info}</p> : null}
                        </div>
                        <input id={id} type="file" className="hidden"
                            disabled={disabled}
                            name={name}
                            accept={accept}
                            multiple={multiple}
                            onChange={changeFile} />
                    </label>
                </div>
                {error && <p className="mt-2 text-sm text-red-600">{errorMessage}</p>}
            </div>
        </Fragment>
    );

};

export default FileUpload;